/**
 * apiErrors — one place that turns a failed API call into operator copy.
 *
 * The portal API answers errors as `{ code, message }` with a terse ENGLISH
 * message meant for logs ("Pass minting failed", "Missing or malformed
 * fields"); the SPA used to print that message verbatim into a Hungarian UI.
 * Known codes map to HU/EN copy here; unknown ones still say something useful
 * (a server hiccup vs. a request the API refused, with the code for support).
 */
import { ApiClientError } from '../api/client';
import type { MessageKey, Translate } from '../i18n/messages';

const COPY_FOR_CODE: Readonly<Record<string, MessageKey>> = {
  MINT_FAILED: 'error.mintFailed',
  INVALID_REQUEST: 'error.invalidRequest',
  MISSING_SERVICE_PAYLOAD: 'error.invalidRequest',
  TEMPLATE_DISABLED: 'error.templateDisabled',
  INVALID_TEMPLATE: 'error.templateDisabled',
  APPLE_NOT_CONFIGURED: 'error.appleNotConfigured',
  APPLE_TEMPLATE_NOT_SUPPORTED: 'error.appleTemplateNotSupported',
  INVALID_VISIBILITY_WINDOW: 'error.visibilityWindow',
  INVALID_POLICY_NUMBER: 'error.policyNumber',
  TRANSIT_NOT_SUPPORTED: 'error.transitNotSupported',
  FLIGHT_NOT_FOUND: 'error.flightNotFound',
  FLIGHT_ENGINE_BUSY: 'error.flightBusy',
  NOT_FOUND: 'error.notFound',
  RATE_LIMITED: 'error.rateLimited',
  INVALID_EMAIL: 'error.invalidEmail',
  INVALID_TOKEN: 'error.linkExpired',
  INVALID_OR_EXPIRED: 'error.linkExpired',
  NO_SESSION: 'error.sessionExpired',
  INVALID_SESSION: 'error.sessionExpired',
  OBJECT_TOO_LARGE: 'error.tooLarge',
};

export function describeApiError(error: unknown, t: Translate): string {
  if (error instanceof ApiClientError) {
    const key = COPY_FOR_CODE[error.body.code];
    if (key !== undefined) return t(key);
    if (error.status >= 500) return t('error.server');
    return t('error.refused', { code: error.body.code || `HTTP ${String(error.status)}` });
  }
  // fetch() rejects with a TypeError when the network or CORS fails.
  if (error instanceof TypeError) return t('error.network');
  // Mock-mode and client-side validation errors already carry operator copy.
  if (error instanceof Error && error.message !== '') return error.message;
  return t('error.unknown');
}

/** The API (or the mock) answered 404 — the thing is gone, not broken. */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 404;
}
